import { useCallback, useEffect, useState } from "react";
import { Scale, Settings2, Inbox, AlarmClock, Archive, FolderOpen } from "lucide-react";
import { api } from "../../lib/api";
import { LegalCasesList } from "./LegalCasesList";
import { LegalNewCase } from "./LegalNewCase";
import { LegalArchive } from "./LegalArchive";
import { LegalCaseView } from "./LegalCaseView";
import { LegalSettings } from "./LegalSettings";

const TITLES = { "legal-cases": "الحالات القانونية", "legal-new": "فتح حالة جديدة", "legal-archive": "أرشيف القضايا" };

const Stat = ({ icon: Icon, label, value, tone, testId, onClick, active }) => (
  <button type="button" onClick={onClick} className={`flex items-center gap-2 rounded-xl border px-3 py-2 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md ${tone} ${active ? "ring-2 ring-offset-1 ring-[#0f1f3d]/40" : ""}`} data-testid={testId}>
    <Icon className="h-4 w-4" /><span className="text-xs font-semibold">{label}</span><b className="font-mono-nums text-lg">{value ?? "—"}</b>
  </button>
);

export const LegalPanel = ({ view, employees, onNavigate }) => {
  const [scope, setScopeRaw] = useState(() => sessionStorage.getItem("legal_scope") || "active");
  const setScope = (s) => { sessionStorage.setItem("legal_scope", s); setScopeRaw(s); };
  const go = (s) => { if (s === "archive") onNavigate("legal-archive"); else { setScope(s); if (view !== "legal-cases") onNavigate("legal-cases"); } };
  const on = (s) => (s === "archive" ? view === "legal-archive" : view === "legal-cases" && scope === s);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [openId, setOpenId] = useState(() => { const v = sessionStorage.getItem("legal_open_once"); sessionStorage.removeItem("legal_open_once"); return v; });
  const [tick, setTick] = useState(0);
  const [settings, setSettings] = useState(false);
  const loadMeta = useCallback(() => api.legalMeta().then(setMeta).catch(() => {}), []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { api.legalStats().then(setStats).catch(() => {}); }, [tick]);
  const refresh = () => setTick((t) => t + 1);

  return (
    <div className="space-y-4" data-testid="legal-panel">
      <div className="relative overflow-hidden rounded-2xl bg-[#0f1f3d] p-5 text-white">
        <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-amber-400/10" />
        <div className="relative flex flex-wrap items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-amber-400/15 ring-1 ring-amber-300/40"><Scale className="h-6 w-6 text-amber-300" /></div>
          <div className="flex-1">
            <p className="text-[11px] font-semibold tracking-wide text-amber-200/80">الشؤون القانونية</p>
            <h2 className="font-heading text-2xl font-extrabold" data-testid="legal-title">{TITLES[view]}</h2>
          </div>
          {meta?.is_owner && <button onClick={() => setSettings(true)} data-testid="legal-settings-btn" className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/20"><Settings2 className="h-4 w-4" /> الممثل القانوني والتصنيفات</button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Stat icon={FolderOpen} label="جارية" value={stats?.active} tone="border-sky-200 bg-sky-50 text-sky-800" testId="legal-stat-active" onClick={() => go("active")} active={on("active")} />
        <Stat icon={Inbox} label="لديّ الآن" value={stats?.mine} tone="border-amber-200 bg-amber-50 text-amber-800" testId="legal-stat-mine" onClick={() => go("mine")} active={on("mine")} />
        <Stat icon={AlarmClock} label="متأخرة" value={stats?.overdue} tone="border-rose-200 bg-rose-50 text-rose-800" testId="legal-stat-overdue" onClick={() => go("overdue")} active={on("overdue")} />
        <Stat icon={Archive} label="في الأرشيف" value={stats?.archive} tone="border-slate-200 bg-slate-50 text-slate-700" testId="legal-stat-archive" onClick={() => go("archive")} active={on("archive")} />
      </div>
      {!meta ? null : view === "legal-new" ? (
        <LegalNewCase meta={meta} employees={employees} onReloadMeta={loadMeta}
          onCreated={(id) => { sessionStorage.setItem("legal_open_once", id); onNavigate("legal-cases"); }} />
      ) : view === "legal-archive" ? (
        <LegalArchive meta={meta} employees={employees} tick={tick} onOpen={setOpenId} onChanged={refresh} />
      ) : (
        <LegalCasesList tick={tick} onOpen={setOpenId} onNew={() => onNavigate("legal-new")} scope={scope} setScope={setScope} onChanged={refresh} />
      )}
      {openId && meta && <LegalCaseView id={openId} meta={meta} employees={employees} onClose={() => { setOpenId(null); refresh(); }} />}
      {settings && <LegalSettings meta={meta} onClose={() => setSettings(false)} onSaved={loadMeta} />}
    </div>
  );
};
